import React, { useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import { SearchIcon } from "../../shared/icons";
import { debounce } from "lodash";
import { screenSizes } from "../../styles/theme";
import { Button } from "../../components/Buttons";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

const SearchContainerWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 75%;
  position: relative;
  @media (max-width: ${screenSizes.L}px) {
    width: 100%;
    margin-bottom: 10px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #091c001a;
  border-radius: 50px;
  font-size: 14px;
  outline: none;
  background: #f4f4f4;
  font-family: "Sora", sans-serif;
  &::placeholder {
    font-size: 16px;
    font-weight: 400;
    color: #091c0099;
  }
  @media (max-width: ${screenSizes.L}px) {
    padding: 10px;
    height: 48px;
  }
`;

const SearchIconContainer = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  cursor: pointer;
  &:hover {
    background-color: #73737315;
    border-radius: 20px;
  }
  svg {
    width: 20px;
    height: 20px;
    stroke: #737373;
    stroke-width: 2;
    stroke-linecap: round;
    &:hover {
      filter: brightness(0) sepia(1) hue-rotate(90deg) saturate(2);
    }
  }
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #f1e5c0;
  padding: 2rem;
  width: 780px;
  height: 184px;
  margin-block: 25px;
  gap: 35px;
  margin-inline: auto;
  @media (max-width: ${screenSizes.L}px) {
    width: 350px;
    gap: 10px;
    display: flex;
    height: 250px;
    flex-direction: column;
    padding: 1rem;
  }
  h2 {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    text-align: left;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 20px;
      line-height: 1.5;
    }
  }
  div {
    input {
      border-color: transparent;
    }
    button {
      border-radius: 80px;
      font-family: Manrope;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      text-align: center;
      width: 160px;
      margin-top: 2px;
    }
  }
  button {
    border-radius: 80px;
    font-size: 14px;
    font-weight: 700;
    width: 100%;
    height: 48px;
    margin-top: -10px;
  }
`;

interface SearchBarProps {
  isFilterSearch?: boolean;
  handleSearchWithFilter?: (
    query: any,
    limit: number,
    page: number,
    blockchain_network: string,
    rank_by: string,
    category_id: string,
    eco_category_id: string
  ) => void;
  isHeaderSearch?: boolean;
  handleSearch?: (query: string) => void;
  searchResults?: any;
  isLoading?: boolean;
  setSearchStr?: any;
}

const AISearchBar: React.FC<SearchBarProps> = ({
  handleSearch,
  isFilterSearch,
  handleSearchWithFilter,
  setSearchStr
}) => {
  const params = useParams();
  const prevParams = useRef(params);
  const [searchQuery, setSearchQuery] = useState(params?.query || "");
  const { network, impactScore, categories, ecoCategories } = useSelector(
    (state: RootState) => state.searchFilter
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchStr(query);
        if (isFilterSearch && handleSearchWithFilter) {
          handleSearchWithFilter(
            query ?? params?.query,
            100,
            1,
            network,
            impactScore,
            categories,
            ecoCategories
          );
        } else {
          handleSearch && handleSearch(query);
        }
      }, 300),
    [handleSearch]
  );

  const handleInputChange = (event: any) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (JSON.stringify(prevParams.current) !== JSON.stringify(params)) {
      setSearchQuery(params?.query || "");
      debouncedSearch(params.query || "");
    }
    prevParams.current = params;
  }, [params]);

  const isMobile = window.innerWidth <= 1024;

  return (
    <SearchInputWrapper>
      <h2>Explore digital assets across chains</h2>
      <SearchContainerWrapper>
        <SearchContainer>
          <SearchInput
            value={searchQuery}
            onChange={handleInputChange}
            placeholder={"Search for digital assets"}
          />
          {/* <SearchIconContainer onClick={()=> debouncedSearch(searchQuery)}>
            <SearchIcon />
          </SearchIconContainer> */}
        </SearchContainer>
        {!isMobile && (
          <Button
            variant="secondary"
            onClick={() => debouncedSearch(searchQuery)}
          >
            AI Search
          </Button>
        )}
      </SearchContainerWrapper>
      {isMobile && (
        <Button
          variant="secondary"
          onClick={() => debouncedSearch(searchQuery)}
        >
          AI Search
        </Button>
      )}
    </SearchInputWrapper>
  );
};

export default AISearchBar;
